import React, { useRef, useCallback, useState, useEffect, useMemo } from 'react';
import moment from 'moment';
import classnames from 'classnames';
import { getMessage } from '@tcc/i18n';

import { categoryTypeColor, getActivitySeasonYear, getInitialValues, isFile } from '../../../../utils/scoutingUtils';
import { getActivityLoading, getCategories, getCategoriesStatus } from '../../../../selectors/scoutingSelectorsNew';
import { isPending, isIdle } from '../../../../utils';
import { getApiUrl } from '../../../../utils/url';
import { useAppDispatch, useAppSelector } from '../../../../utils/useAppSelector';
import { getCurrentSeasonYear } from '../../../../selectors/seasonSelectors';
import { getModalMode } from '../../../../selectors/modalSelectors';
import { getFieldUuid } from '../../../../selectors';
import { useClickOutside } from '../../../../hooks/useClickOutside'
import { closeModal } from '../../../../actions/modalActions';
import { setPlacingPin } from '../../../../actions/mapActions';
import { trackHeapEvent } from '../../../../actions/analyticsActions';
import {
  reqCategories,
  reqNewActivity,
  setSelectedActivity,
  requestDeleteActivity,
  reqUpdateEnhancedActivity,
} from '../../../../actions/scoutingActions';

import {
  FilesEditView,
  ScoutingFormFooter,
  ScoutingFormDetails,
  CategorySelector,
} from '.';

import {
  ShapeType,
  ModalMode,
  ActivityColor,
  CategoryType,
  ActivityAsset,
  EnhancedScoutingPinPayload,
  CategoryAttributeLevelSelected,
  EnhancedActivity,
  Activity,
} from '../../../../types';

import ConfirmationModal from '../../../Modal/ConfirmationModal';

import styles from './scoutingForm.css';
import { ModalWrapper } from '../../../../components/ModalWrapper/ModalWrapper';

type EnhancedScoutingFormProps = {
  index: number;
}

export enum FormViews {
  INDEX = 'INDEX',
  FILES = 'FILES',
}

export enum ModalTypes {
  DELETE_PIN = 'DELETE_PIN',
  CONFIRM_CLOSE = 'CONFIRM_CLOSE',
}

export const EnhancedScoutingForm = ({ index }: EnhancedScoutingFormProps) => {
  const dispatch = useAppDispatch();
  const state = useAppSelector(state => state);

  const { activity: untypedActivity } = useAppSelector(state => state.modal.meta);
  const activity = untypedActivity as Activity | EnhancedActivity
  const modalMode = useAppSelector(getModalMode);
  const isSubmitting = useAppSelector(getActivityLoading);
  const currentSeasonYear = useAppSelector(state => getCurrentSeasonYear(state, { index }));

  const isAddingPin = modalMode === ModalMode.ADD_ENHANCED_PIN; // is either add pin or edit pin

  const fieldUuid = getFieldUuid(state);
  const categories = useAppSelector(getCategories);
  const categoriesLoadingStatus = useAppSelector(getCategoriesStatus);
  const isLoadingCategories = isPending(categoriesLoadingStatus);

  const initialValues = getInitialValues(activity);

  const [formView, setFormView] = useState<FormViews>(FormViews.INDEX);
  const [title, setTitle] = useState(initialValues.title);
  const [note, setNote] = useState(initialValues.note);
  const [isSeasonal, setIsSeasonal] = useState(!initialValues.permanent);
  const [date, setDate] = useState(initialValues.date ?? moment(Date.now()).set('year', parseInt(currentSeasonYear, 10)).toISOString());
  const [soilSample, setSoilSample] = useState(initialValues.soilSample);
  const [categoryTypeSelected, setCategoryTypeSelected] = useState<CategoryType | null>(initialValues.categoryTypeSelected);
  const [attributesSelected, setAttributesSelected] = useState<CategoryAttributeLevelSelected>(initialValues.attributesSelected);
  const [selectedAssets, setSelectedAssets] = useState<(ActivityAsset | File)[]>(initialValues.assets);

  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [modalType, setModalType] = useState<ModalTypes.DELETE_PIN | ModalTypes.CONFIRM_CLOSE | null>(null);

  const formWrapperRef = useRef(null);
  const currentFileIndexEdit = useRef<number>(0);

  const previews: string[] = useMemo(
    () => selectedAssets
      .filter(file => isFile(file) ? true : !file.deleted)
      .map(file => isFile(file) ? URL.createObjectURL(file) : getApiUrl(`/api/tenderfoot${file.uri}`)
      ), [selectedAssets]);

  const coordinates = useAppSelector(state => {
    const modalMeta = state.modal.meta;
    const activity = modalMeta.activity as Activity | EnhancedActivity

    if (isAddingPin && modalMeta.lng && modalMeta.lat) return [modalMeta.lng, modalMeta.lat];

    return activity.location?.coordinates as number[] || [0, 0];
  });

  useEffect(() => {
    if (isAddingPin) {
      dispatch(trackHeapEvent('View Scouting Pin'));
    }
  }, [dispatch, isAddingPin]);

  useEffect(() => {
    if (isIdle(categoriesLoadingStatus)) {
      dispatch(reqCategories());
    }
  }, [dispatch, categoriesLoadingStatus]);

  const onCloseModal = useCallback(() => {
    dispatch(closeModal(index));
    dispatch(setSelectedActivity(index, null));
    if (isAddingPin) {
      dispatch(setPlacingPin(false));
    }
  }, [index, dispatch, isAddingPin]);

  useClickOutside(isSubmitting ? null : formWrapperRef, () => handleOutsideClick());

  const handleOutsideClick = useCallback(() => {
    openModal(ModalTypes.CONFIRM_CLOSE);
  }, []);

  const onAddFiles = (newFiles: File[]) => {
    setSelectedAssets(prevFiles => [...prevFiles, ...newFiles]);
  }

  const onDeleteFile = (index: number) => {
    setSelectedAssets(prevFiles => {
      const copy = [...prevFiles];
      const image = { ...copy[index] };

      if (isFile(image)) {
        copy.splice(index, 1);
      } else {
        image.deleted = true;
        copy[index] = image;
      }

      return copy;
    });
  };

  const onFilesEdit = (editIndex: number) => {
    currentFileIndexEdit.current = editIndex;
    setFormView(FormViews.FILES);
  };

  const onFilesEditClose = () => {
    currentFileIndexEdit.current = 0;
    setFormView(FormViews.INDEX);
  };

  const onReplaceFiles = (index: number, newFile: File) => {
    setSelectedAssets(prevFiles => {
      const copy = [...prevFiles];
      const image = copy[index];

      if (isFile(image)) {
        copy[index] = newFile;
      } else {
        const prevImage = { ...image };
        prevImage.deleted = true;
        copy.push(prevImage);
        copy[index] = newFile;
      }

      return copy;
    });
  }

  const toggleSoilSample = () => {
    setSoilSample(prevSoilSample => !prevSoilSample);
  };

  const onCreate = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const payload: EnhancedScoutingPinPayload & { index: number } = {
      title: title ?? '',
      comments: [{ note }],
      color: ActivityColor.BLUE,
      field: fieldUuid,
      permanent: !isSeasonal,
      scoutedAt: date,
      location: {
        type: ShapeType.POINT,
        coordinates,
      },
      categoryType: categoryTypeSelected ?? 'OTHER',
      categoryAttributeIds: Object.values(attributesSelected),
      soilSample,
      index,
    };
    const assets = selectedAssets.length > 0 ? selectedAssets : null;
    if (assets) dispatch(trackHeapEvent('Scouting PIN - Upload Photo'));
    dispatch(reqNewActivity(payload, assets, true));
  }

  const onEdit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const id = activity?.id ?? '';
    const permanent = !isSeasonal;
    const scoutedAt = date;

    const payload: Omit<EnhancedScoutingPinPayload, 'comments'> & { index: number } = {
      id,
      title: title ?? '',
      color: activity?.color ?? ActivityColor.BLUE,
      field: fieldUuid,
      location: {
        type: ShapeType.POINT,
        coordinates,
      },
      permanent: !isSeasonal,
      categoryType: categoryTypeSelected,
      categoryAttributeIds: Object.values(attributesSelected),
      scoutedAt,
      soilSample,
      index,
    };

    const comment = { ...activity?.comments[0], note };
    const fromToYear = { // if pin date edited, we need to also update it in the store to index it by the year it has been moved to
      from: getActivitySeasonYear({ permanent: activity?.permanent ?? permanent }, activity?.scoutedAt ?? scoutedAt),
      to: getActivitySeasonYear({ permanent }, scoutedAt),
    };

    dispatch(reqUpdateEnhancedActivity(payload, comment, selectedAssets, fromToYear));

  }

  const onDelete = () => {
    dispatch(requestDeleteActivity(activity, { index }));
  };

  const openModal = (modalType: ModalTypes) => {
    setModalType(modalType)
    setConfirmationModal(true);
  }

  const modalConfirmationButtons = {
    DELETE_PIN: getMessage('regions.report.modal.delete'),
    CONFIRM_CLOSE: getMessage('regions.report.modal.close')
  }

  const modalTitles = {
    DELETE_PIN: getMessage('enhancedScouting.form.confirmationModal.deleteScoutingPin'),
    CONFIRM_CLOSE: getMessage('confirmationModal.title')
  }

  const submitModal = () => {
    if (modalType === ModalTypes.CONFIRM_CLOSE) {
      onCloseModal();
      setModalType(null);
      setConfirmationModal(false);
    }
    if (modalType === ModalTypes.DELETE_PIN) {
      onDelete();
      setModalType(null);
      setConfirmationModal(false);
    }
  }

  const categoryTypes: CategoryType[] = ['DISEASE', 'INSECT', 'WEED', 'NUTRIENT', 'WATER', 'OTHER'];
  const isCreatingPin = (isAddingPin && !activity?.id);
  const isLoading = isLoadingCategories || isSubmitting;

  return (
    <form
        ref={formWrapperRef}
        className={styles.enhancedScoutingForm}
        onSubmit={isCreatingPin ? onCreate : onEdit}
        data-test-id="enhanced-scouting-form"
      >
        {formView === FormViews.INDEX && (
          <>
            <div className={styles.enhancedScoutingHeader}>
              <div className={styles.dotColorHeader} style={{ backgroundColor: categoryTypeSelected ? categoryTypeColor(categoryTypeSelected) : undefined }} />
              <div className={classnames(
                styles.ScoutingFormTitle,
                styles.ScoutingFormTitleEditing)
              }>
                <h3 className={styles.enhancedScoutingTitle}>{title}</h3>
              </div>
              <div className={styles.dotColorHeader} style={{ visibility: 'hidden', marginLeft: 10, marginRight: 0 }} />
            </div>
            <ScoutingFormDetails
              title={title ?? ''}
              note={note}
              coords={coordinates}
              isSeasonal={isSeasonal}
              dateSelected={date}
              soilSample={soilSample}
              previews={previews}
              isEditing={!isCreatingPin}
              isLoading={isLoading}
              setDate={setDate}
              setNote={setNote}
              setIsSeasonal={setIsSeasonal}
              toggleSoilSample={toggleSoilSample}
              onDelete={() => openModal(ModalTypes.DELETE_PIN)}
              onTitleEdit={setTitle}
              onFilesSelection={onAddFiles}
              onFilesEdit={onFilesEdit}
            >
              <CategorySelector
                categoryTypes={categoryTypes}
                categoryTypeSelected={categoryTypeSelected}
                attributes={categoryTypeSelected ? (categories[categoryTypeSelected] ?? null) : null}
                attributesSelected={attributesSelected}
                setAttributesSelected={setAttributesSelected}
                setCategorySelected={setCategoryTypeSelected}
                loading={isLoadingCategories}
              />
            </ScoutingFormDetails>
            <ScoutingFormFooter
              onCancel={() => openModal(ModalTypes.CONFIRM_CLOSE)}
              submitting={isSubmitting}
              disabled={isLoading || !title || title.length === 0} />
            {confirmationModal && (
              <ModalWrapper>
                <ConfirmationModal
                  handleClickConfirm={submitModal}
                  handleClickGoBack={() => setConfirmationModal(false)}
                  handleClickCancel={() => setConfirmationModal(false)}
                  confirmButtonText={modalType ? modalConfirmationButtons[modalType] ?? undefined : undefined}
                  className={styles.confirmationModal}
                  title={(modalType && modalTitles[modalType]) ?? ''}
                  confirmButtonMode={modalType === ModalTypes.DELETE_PIN ? 'negative' : 'primary'}
                />
              </ModalWrapper>
            )}
          </>
        )}

        {formView === FormViews.FILES && (
          <FilesEditView
            previews={previews}
            onClose={onFilesEditClose}
            onDelete={onDeleteFile}
            onAdd={onAddFiles}
            onReplace={onReplaceFiles}
            indexToEdit={currentFileIndexEdit.current}
            confirmationModal={confirmationModal}
            setConfirmationModal={setConfirmationModal}
          />
        )}
      </form>
  )
};
